const ID_SEGMENT = 0x18538067;
const ID_CLUSTER = 0x1f43b675;
const ID_TIMECODE = 0xe7;
const ID_SIMPLE_BLOCK = 0xa3;
const ID_BLOCK_GROUP = 0xa0;
const ID_BLOCK = 0xa1;
const UNKNOWN_SIZE = new Uint8Array([0x01, 0xff, 0xff, 0xff, 0xff, 0xff, 0xff, 0xff]);

interface Vint {
  value: number;
  length: number;
  unknown: boolean;
}

function readId(buf: Uint8Array, pos: number): Vint | null {
  if (pos >= buf.length) return null;
  const first = buf[pos]!;
  let length = 1;
  while (length <= 4 && !(first & (0x80 >> (length - 1)))) length++;
  if (length > 4) throw new Error('invalid EBML id');
  if (pos + length > buf.length) return null;
  let value = 0;
  for (let i = 0; i < length; i++) value = value * 256 + buf[pos + i]!;
  return { value, length, unknown: false };
}

function readSize(buf: Uint8Array, pos: number): Vint | null {
  if (pos >= buf.length) return null;
  const first = buf[pos]!;
  let length = 1;
  while (length <= 8 && !(first & (0x80 >> (length - 1)))) length++;
  if (length > 8) throw new Error('invalid EBML size');
  if (pos + length > buf.length) return null;
  let value = first & (0xff >> length);
  let allOnes = value === (0xff >> length);
  for (let i = 1; i < length; i++) {
    const byte = buf[pos + i]!;
    value = value * 256 + byte;
    if (byte !== 0xff) allOnes = false;
  }
  return { value, length, unknown: allOnes };
}

function readUint(buf: Uint8Array, start: number, length: number): number {
  let value = 0;
  for (let i = 0; i < length; i++) value = value * 256 + buf[start + i]!;
  return value;
}

function timecodeElement(value: number): Uint8Array {
  const out = new Uint8Array(10);
  out[0] = ID_TIMECODE;
  out[1] = 0x88;
  let v = Math.max(0, Math.round(value));
  for (let i = 9; i >= 2; i--) {
    out[i] = v % 256;
    v = Math.floor(v / 256);
  }
  return out;
}

/**
 * Rewrites a MediaRecorder WebM byte stream so it can be appended to an earlier one:
 * everything before the first Cluster is dropped and every Cluster timecode is shifted
 * by `offset`. With a null offset the stream passes through untouched; either way the
 * last block timecode written is tracked so the next recorder can continue after it.
 */
export class WebmStitcher {
  lastTimecode = -1;
  private pending = new Uint8Array(0);
  private seenCluster = false;
  private clusterTimecode = 0;

  constructor(private readonly offset: number | null, private readonly keepHeader = false) {}

  get continuing(): boolean {
    return this.offset !== null;
  }

  private get dropsHeader(): boolean {
    return this.continuing && !this.keepHeader;
  }

  push(chunk: Uint8Array): Uint8Array {
    const buf = this.concat(this.pending, chunk);
    const out: Uint8Array[] = [];
    let pos = 0;

    while (pos < buf.length) {
      const id = readId(buf, pos);
      if (!id) break;
      const size = readSize(buf, pos + id.length);
      if (!size) break;
      const headerEnd = pos + id.length + size.length;

      if (id.value === ID_SEGMENT || id.value === ID_CLUSTER) {
        if (id.value === ID_CLUSTER) {
          this.seenCluster = true;
          if (this.continuing) {
            out.push(buf.subarray(pos, pos + id.length), UNKNOWN_SIZE);
          } else {
            out.push(buf.subarray(pos, headerEnd));
          }
        } else if (!this.dropsHeader) {
          out.push(buf.subarray(pos, headerEnd));
        }
        pos = headerEnd;
        continue;
      }

      if (size.unknown) throw new Error(`unexpected unknown-size element 0x${id.value.toString(16)}`);
      const end = headerEnd + size.value;
      if (end > buf.length) break;

      if (id.value === ID_TIMECODE && this.seenCluster) {
        const original = readUint(buf, headerEnd, size.value);
        this.clusterTimecode = original + (this.offset ?? 0);
        out.push(this.continuing ? timecodeElement(this.clusterTimecode) : buf.subarray(pos, end));
      } else if (id.value === ID_SIMPLE_BLOCK || id.value === ID_BLOCK_GROUP) {
        this.trackBlock(buf, id.value, headerEnd, end);
        out.push(buf.subarray(pos, end));
      } else if (this.dropsHeader && (!this.seenCluster || !this.isClusterChild(id.value))) {
      } else {
        out.push(buf.subarray(pos, end));
      }
      pos = end;
    }

    this.pending = buf.slice(pos);
    return this.concat(...out);
  }

  private isClusterChild(id: number): boolean {
    return id === ID_TIMECODE || id === ID_SIMPLE_BLOCK || id === ID_BLOCK_GROUP || id === 0xa7 || id === 0xab || id === 0xec;
  }

  private trackBlock(buf: Uint8Array, id: number, start: number, end: number) {
    let blockStart = start;
    if (id === ID_BLOCK_GROUP) {
      let pos = start;
      blockStart = -1;
      while (pos < end) {
        const childId = readId(buf, pos);
        const childSize = childId && readSize(buf, pos + childId.length);
        if (!childId || !childSize) return;
        const dataStart = pos + childId.length + childSize.length;
        if (childId.value === ID_BLOCK) {
          blockStart = dataStart;
          break;
        }
        pos = dataStart + childSize.value;
      }
      if (blockStart < 0) return;
    }
    const track = readSize(buf, blockStart);
    if (!track || blockStart + track.length + 2 > end) return;
    const raw = (buf[blockStart + track.length]! << 8) | buf[blockStart + track.length + 1]!;
    const relative = raw & 0x8000 ? raw - 0x10000 : raw;
    this.lastTimecode = Math.max(this.lastTimecode, this.clusterTimecode + relative);
  }

  private concat(...parts: Uint8Array[]): Uint8Array {
    if (parts.length === 1) return parts[0]!.slice();
    const total = parts.reduce((n, p) => n + p.length, 0);
    const out = new Uint8Array(total);
    let offset = 0;
    for (const part of parts) {
      out.set(part, offset);
      offset += part.length;
    }
    return out;
  }
}
